import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import Cookies from "js-cookie";

import { BlocksContent, IconButton } from "@global";
import { useAnnouncementBarQuery } from "@query";

const CYCLE_SPEED_MS = 6000;
const FADE_SPEED_MS = 300;
const DISMISSED_COOKIE_PREFIX = "announcementBarDismissed-";
const HEIGHT_CSS_VAR = "--announcement-bar-height";

const Wrapper = styled.div<{ backgroundColor?: string }>`
  box-sizing: border-box;
  width: 100%;
  position: fixed;
  top: env(safe-area-inset-top, 0px);
  left: 0;
  z-index: 23;
  background-color: ${({ backgroundColor }) =>
    backgroundColor || "var(--theme-primary-color)"};
  padding: 10px 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  transition: background-color ${FADE_SPEED_MS}ms ease-in-out;

  .block-content {
    width: auto;
  }

  .block-content p {
    margin: 0 !important;
    font-size: 14px;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
`;

const MessageWrapper = styled.div<{ visible: boolean }>`
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity ${FADE_SPEED_MS}ms ease-in-out;
`;

const AnnouncementBar = () => {
  const announcementBarData = useAnnouncementBarQuery();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const messages = announcementBarData?.messages;
  const hasMessages = !!messages && messages.length > 0;
  const hasMultipleMessages = !!messages && messages.length > 1;
  const isShowing = hasMessages && !dismissed;

  // Check for a previous dismissal of this exact announcement bar (keyed by
  // document id so a content change surfaces the bar again for everyone).
  useEffect(() => {
    if (!announcementBarData?._id) return;

    const isDismissed = Cookies.get(
      `${DISMISSED_COOKIE_PREFIX}${announcementBarData._id}`
    );
    if (isDismissed) setDismissed(true);
  }, [announcementBarData?._id]);

  useEffect(() => {
    if (!hasMultipleMessages) return;

    const interval = setInterval(() => {
      setVisible(false);

      setTimeout(() => {
        setActiveIndex(current => {
          const nextIndex = current + 1;
          return nextIndex >= (messages?.length ?? 0) ? 0 : nextIndex;
        });
        setVisible(true);
      }, FADE_SPEED_MS);
    }, CYCLE_SPEED_MS);

    return () => clearInterval(interval);
  }, [hasMultipleMessages, messages?.length]);

  // The bar is fixed to the very top of the viewport, so the fixed nav needs
  // to know its real (content-dependent, can reflow at different widths) height
  // in order to sit directly below it instead of underneath it. Keep a CSS
  // var on the root element in sync with the bar's actual rendered height,
  // and zero it out whenever the bar isn't showing.
  useLayoutEffect(() => {
    const setOffset = (height: number) => {
      document.documentElement.style.setProperty(HEIGHT_CSS_VAR, `${height}px`);
    };

    if (!isShowing || !wrapperRef.current) {
      setOffset(0);
      return;
    }

    const node = wrapperRef.current;
    // getBoundingClientRect().bottom (not offsetHeight) so the offset still
    // accounts for the safe-area inset the bar itself is offset by on notched
    // phones, keeping the fixed nav flush against the bar in every case.
    const updateOffset = () => setOffset(node.getBoundingClientRect().bottom);

    updateOffset();

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updateOffset);
    observer.observe(node);

    return () => {
      observer.disconnect();
      setOffset(0);
    };
  }, [isShowing]);

  if (!isShowing) return null;

  const activeMessage = messages[activeIndex];

  if (!activeMessage) return null;

  const handleDismiss = () => {
    if (announcementBarData?._id) {
      Cookies.set(
        `${DISMISSED_COOKIE_PREFIX}${announcementBarData._id}`,
        "true"
      );
    }
    setDismissed(true);
  };

  return (
    <Wrapper
      ref={wrapperRef}
      backgroundColor={activeMessage.backgroundColor?.value ?? undefined}
    >
      <MessageWrapper visible={visible}>
        <BlocksContent blocks={activeMessage.text?._rawBlocks} />
      </MessageWrapper>
      <CloseButton>
        <IconButton
          icon="close"
          alt="Dismiss announcement"
          onClick={handleDismiss}
        />
      </CloseButton>
    </Wrapper>
  );
};

export default AnnouncementBar;
